import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useConfig } from "./ConfigProvider";
import { GetParameter, IsWorkingDay, GetLicense, GetParameterForSite, GetNavBarItems, DeleteNavBarItem, AddNavBarItem, GetHomePageSliderItems, AddHomePageSliderItem, UpdateHomePageSliderItem, DeleteHomePageSliderItem, GetFooterElements, AddFooterItem, AddFooterHeader, UpdateFooterItem, UpdateFooterHeader, DeleteFooterItem, DeleteFooterHeader } from "../Services/ParameterService";
import { useMediaQuery } from "react-responsive";
import { Licenses } from "../Data/Licenses.ts";

const ParamContext = createContext()

export const useParams = () => {
    return useContext(ParamContext)
}

export const ParamsProvider = ({ children }) => {
    const { api } = useConfig()
    const isMobile = useMediaQuery({ maxWidth: 1400 })
    const navBarItemsCache = useRef(null) //cache elementów paska nawigacji, by nie pobierać przy każdej zmianie zakładki
    const homePageSliderItemsCache = useRef(null) //cache elementów slidera, by nie pobierać przy każdej zmianie zakładki
    const footerElementsCache = useRef(null) //cache elementów stopki, by nie pobierać przy każdej zmianie zakładki
    const [availabilityCutoffTime, setAvailabilityCutoffTime] = useState('15:00')//Godzina graniczna dla dostępności (hh:mm)

    const [indexMask, setIndexMask] = useState(() => { //Maska dla indexu
        return localStorage.getItem('indexMask') || null
    })
    const [priceListExportMaxArticle, setPriceListExportMaxArticle] = useState(() => {
        return localStorage.getItem('priceListExportMaxArticle') || 0
    })

    const [geolocationApiKey, setGeolocationApiKey] = useState('') //Klucz do API google
    const [searchingQueryDelay, setSearchingQueryDelay] = useState(500) //Opóznienie w wyszukiwaniu produktów (w milisekundach)
    const [searchingQueryCharNumber, setSearchingQueryCharNumber] = useState(1) //Ilość znaków, po których następuje szukanie produktów
    const [eInvoiceEmail0101, setEInvoiceEmail0101] = useState('') //email do zgłośeń efaktur dla oddziału 0101
    const [eInvoiceEmail0201, setEInvoiceEmail0201] = useState('')//email do zgłośeń efaktur dla oddziału 0201
    const [license, setLicense] = useState(localStorage.getItem('APP_LICENSE') || '')// licencja programu
    const [autoLogOutTime, setAutoLogoutTime] = useState(5) //czas do automatycznego wylogowania podawany w minutach
    const [basketItemsCountLimit, setBasketItemsCountLimit] = useState(200) //Limit ilości pozycji w koszyku
    const [agreementsVersion, setAgreementsVersion] = useState(1) //Wersja regulaminu oraz OWS
    const [aiDescriptionIsActive, setAIDescriptionIsActive] = useState(false) //Czy funkcja opisu AI jest aktywna
    const [productFiltersDebounceTimeout, setProductFiltersDebounceTimeout] = useState(1000) //Czas w milisekundach opóźniający pobieranie produktów po zastosowaniu filtrów

    useEffect(() => {
        handleGetAutoLogoutTime()
        handleGetProductFiltersDebounceTimeout()
        const storedIndexMask = localStorage.getItem('indexMask')
        if (!storedIndexMask) {
            handleGetIndexMask()
        }
        const storedPriceListExportMaxArticle = localStorage.getItem('priceListExportMaxArticle')
        if (!storedPriceListExportMaxArticle) {
            handleGetPriceListExportMaxArticle()
        }
        handleGetBasketItemsCountLimit()
        handleGetGeolocationKey()
        handleGetAvailabilityCutoffTime()
        handleGetSearchingQueryDelay()
        handleGetSearchingQueryCharNumber()
        handleGetEInvoiceEmails()
        handleGetAgreementsVersion()
        handleGetAIDescriptionIsActive()
    }, [api])

    /**
     * DK : Bazowa nazwa aplikacji zależna od licencji.
     * Samo ustawienie document.title jest w AuthProvider, bo tam znany jest wybrany kontrahent.
     */
    const appTitle = (() => {
        switch (license) {
            case Licenses.TgInstalacje:
                return "TG Instalacje"
            case Licenses.Ferrodo:
                return "E-Handel FC"
            case Licenses.Hydraulik:
                return "E-Handel Hydraulik"
            case Licenses.Carmen:
                return "E-Handel Carmen"
            default:
                return "E-Handel"
        }
    })()

    useEffect(() => {
        handleGetLicense()
        updateIcons()
    }, [license])

    const updateIcons = () => {
        const updateIcon = () => {
            const favicon = document.querySelector("link[rel='icon']");
            const appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']");
            const darkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;

            if (favicon) {
                switch (license) {
                    case Licenses.Ferrodo:
                        favicon.href = darkMode ? "/ferrodo-dark.ico" : "/ferrodo-light.ico";
                        break;
                    case Licenses.Hydraulik:
                        favicon.href = darkMode ? "/ferrodo-dark.ico" : "/ferrodo-light.ico";
                        break;
                }
            }

            if (appleTouchIcon) {
                switch (license) {
                    case Licenses.Ferrodo:
                        appleTouchIcon.href = darkMode ? "/ferrodo-dark.ico" : "/ferrodo-light.ico";
                        break;
                    case Licenses.Hydraulik:
                        appleTouchIcon.href = darkMode ? "/ferrodo-dark.ico" : "/ferrodo-light.ico";
                        break;
                }
            }
        };
        // Ustaw favicon na początku
        updateIcon();

        // Nasłuchuj zmiany trybu kolorów
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        mediaQuery.addEventListener("change", updateIcon);

        // Czyszczenie po zakończeniu komponentu
        return () => {
            mediaQuery.removeEventListener("change", updateIcon);
        };
    }

    const handleGetIndexMask = async () => {
        const res = await GetParameter(api, 'IndexMask', 'char', false)
        if (res.data) {
            setIndexMask(res.data)
            localStorage.setItem('indexMask', res.data)
        }
    }
    const handleGetPriceListExportMaxArticle = () => {
        GetParameter(api, 'PriceListExportMaxArticle', 'num', false).then(res => {
            setPriceListExportMaxArticle(res.data)
            localStorage.setItem('priceListExportMaxArticle', res.data)
        })
            .catch(e => {
                console.log(e)
            })
    }
    const handleCheckIsWorkingDay = async (date) => {
        try {
            const res = await IsWorkingDay(api, date)
            return res.data
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const handleGetBasketItemsCountLimit = () => {
        GetParameter(api, 'BasketItemsCountLimit', 'num', false).then(res => {
            setBasketItemsCountLimit(res.data)
        }).catch(e => {
            console.log(e)
        })
    }

    const handleGetGeolocationKey = () => {
        GetParameter(api, 'GeolocationApiKey', 'char', true).then(res => {
            setGeolocationApiKey(res.data)
        }).catch(e => {
            console.log(e)
        })
    }

    const handleGetAvailabilityCutoffTime = () => {
        GetParameter(api, 'AvailabilityCutoffTime', 'char', false).then(res => {
            setAvailabilityCutoffTime(res.data)
        }).catch(e => {
            console.log(e)
            setAvailabilityCutoffTime(null)
        })
    }

    const handleGetSearchingQueryDelay = () => {
        GetParameter(api, 'SearchingQueryDelay', 'num').then(res => {
            setSearchingQueryDelay(res.data)
        }).catch(e => {
            console.log(e)
            setSearchingQueryDelay(500)
        })
    }

    const handleGetSearchingQueryCharNumber = () => {
        GetParameter(api, 'SearchingQueryCharNumber', 'num').then(res => {
            setSearchingQueryCharNumber(res.data)
        }).catch(e => {
            console.log(e)
            setSearchingQueryCharNumber(1)
        })
    }

    const handleGetEInvoiceEmails = () => {
        GetParameterForSite(api, 'eInvoiceEmail', 'char', '0101').then(res => {
            setEInvoiceEmail0101(res.data)
        }).catch(e => {
            console.log(e)
        })
        GetParameterForSite(api, 'eInvoiceEmail', 'char', '0201').then(res => {
            setEInvoiceEmail0201(res.data)
        }).catch(e => {
            console.log(e)
        })
    }

    const handleGetLicense = async () => {
        try {
            const res = await GetLicense(api)
            setLicense(res.data)
            localStorage.setItem('APP_LICENSE', res.data)
        }
        catch (error) {
            console.log(error)
            setLicense('')
            localStorage.setItem('APP_LICENSE', '')
        }
    }

    const handleGetAutoLogoutTime = () => {
        GetParameter(api, 'AutoLogOutTime', 'num', false).then(res => {
            setAutoLogoutTime(res.data)
        }).catch(e => {
            console.log(e)
            setAutoLogoutTime(20)
        })
    }

    const handleGetAgreementsVersion = () => {
        GetParameter(api, 'AgreementsVersion', 'num', false).then(res => {
            setAgreementsVersion(res.data)
        }).catch(e => {
            console.log(e)
        })
    }

    const handleGetAIDescriptionIsActive = () => {
        GetParameter(api, 'AIDescriptionIsActive', 'bool', false).then(res => {
            setAIDescriptionIsActive(res.data)
        }).catch(e => {
            console.log(e)
        })
    }

    const handleGetProductFiltersDebounceTimeout = () => {
        GetParameter(api, 'ProductFiltersDebounceTimeout', 'num', false).then(res => {
            setProductFiltersDebounceTimeout(res.data)
        }).catch(e => {
            console.log(e)
        })
    }

    const handleGetNavBarItems = async (forceRefresh = false) => {
        if (!forceRefresh && navBarItemsCache.current !== null) {
            return navBarItemsCache.current
        }
        try {
            const res = await GetNavBarItems(api)
            navBarItemsCache.current = res.data
            return res.data
        }
        catch (error) {
            console.log(error)
            return []
        }
    }

    const handleGetHomePageSliderItems = async (forceRefresh = false) => {
        if (!forceRefresh && homePageSliderItemsCache.current !== null) {
            return homePageSliderItemsCache.current
        }
        try {
            const res = await GetHomePageSliderItems(api)
            homePageSliderItemsCache.current = res.data
            return res.data
        }
        catch (error) {
            console.log(error)
            return []
        }
    }

    const handleAddHomePageSliderItem = async (item) => {
        try {
            const res = await AddHomePageSliderItem(api, item)
            if (res.data) {
                homePageSliderItemsCache.current = null //unieważniamy cache, by przy odświeżeniu pobrać element z nadanym Id
            }
            return res.data
        }
        catch (error) {
            console.log(error)
            return false
        }
    }

    const handleUpdateHomePageSliderItem = async (item) => {
        try {
            const res = await UpdateHomePageSliderItem(api, item)
            if (res.data && homePageSliderItemsCache.current !== null) {
                homePageSliderItemsCache.current = homePageSliderItemsCache.current.map(i => i.id === item.id ? { ...i, ...item } : i)
            }
            return res.data
        }
        catch (error) {
            console.log(error)
            return false
        }
    }

    const handleDeleteHomePageSliderItem = async (id) => {
        try {
            const res = await DeleteHomePageSliderItem(api, id)
            if (res.data && homePageSliderItemsCache.current !== null) {
                homePageSliderItemsCache.current = homePageSliderItemsCache.current.filter(i => i.id !== id)
            }
            return res.data
        }
        catch (error) {
            console.log(error)
            return false
        }
    }

    const handleGetFooterElements = async (forceRefresh = false) => {
        if (!forceRefresh && footerElementsCache.current !== null) {
            return footerElementsCache.current
        }
        try {
            const res = await GetFooterElements(api)
            footerElementsCache.current = res.data
            return res.data
        }
        catch (error) {
            console.log(error)
            return []
        }
    }

    const handleAddFooterItem = async (item) => {
        try {
            const res = await AddFooterItem(api, item)
            if (res.data) {
                footerElementsCache.current = null //unieważniamy cache, by przy odświeżeniu pobrać pozycję z nadanym ItemId
            }
            return res.data
        }
        catch (error) {
            console.log(error)
            return false
        }
    }

    const handleAddFooterHeader = async (header) => {
        try {
            const res = await AddFooterHeader(api, header)
            if (res.data) {
                footerElementsCache.current = null //unieważniamy cache, by przy odświeżeniu pobrać nagłówek z nadanym HeaderId
            }
            return res.data
        }
        catch (error) {
            console.log(error)
            return false
        }
    }

    const handleUpdateFooterItem = async (item) => {
        try {
            const res = await UpdateFooterItem(api, item)
            if (res.data) {
                footerElementsCache.current = null //unieważniamy cache, by przy odświeżeniu pobrać aktualny stan
            }
            return res.data
        }
        catch (error) {
            console.log(error)
            return false
        }
    }

    const handleUpdateFooterHeader = async (header) => {
        try {
            const res = await UpdateFooterHeader(api, header)
            if (res.data) {
                footerElementsCache.current = null //unieważniamy cache, by przy odświeżeniu pobrać aktualny stan
            }
            return res.data
        }
        catch (error) {
            console.log(error)
            return false
        }
    }

    const handleDeleteFooterItem = async (itemId) => {
        try {
            const res = await DeleteFooterItem(api, itemId)
            if (res.data) {
                footerElementsCache.current = null //unieważniamy cache, by przy odświeżeniu pobrać aktualny stan
            }
            return res.data
        }
        catch (error) {
            console.log(error)
            return false
        }
    }

    const handleDeleteFooterHeader = async (headerId) => {
        try {
            const res = await DeleteFooterHeader(api, headerId)
            if (res.data) {
                footerElementsCache.current = null //unieważniamy cache, by przy odświeżeniu pobrać aktualny stan
            }
            return res.data
        }
        catch (error) {
            console.log(error)
            return false
        }
    }

    const handleAddNavBarItem = async (item) => {
        try {
            const res = await AddNavBarItem(api, item)
            return res.data
        }
        catch (error) {
            console.log(error)
            return false
        }
    }

    const handleDeleteNavBarItem = async (id) => {
        try {
            const res = await DeleteNavBarItem(api, id)
            if (res.data && navBarItemsCache.current !== null) {
                navBarItemsCache.current = navBarItemsCache.current.filter(item => item.id !== id)
            }
            return res.data
        }
        catch (error) {
            console.log(error)
            return false
        }
    }

    return <ParamContext.Provider value={{
        indexMask,
        priceListExportMaxArticle,
        geolocationApiKey,
        availabilityCutoffTime,
        searchingQueryDelay,
        searchingQueryCharNumber,
        eInvoiceEmail0101,
        eInvoiceEmail0201,
        license,
        appTitle,
        isMobile,
        autoLogOutTime,
        basketItemsCountLimit,
        agreementsVersion,
        aiDescriptionIsActive,
        productFiltersDebounceTimeout,
        handleCheckIsWorkingDay,
        handleGetLicense,
        handleGetNavBarItems,
        handleAddNavBarItem,
        handleDeleteNavBarItem,
        handleGetHomePageSliderItems,
        handleAddHomePageSliderItem,
        handleUpdateHomePageSliderItem,
        handleDeleteHomePageSliderItem,
        handleGetFooterElements,
        handleAddFooterItem,
        handleAddFooterHeader,
        handleUpdateFooterItem,
        handleUpdateFooterHeader,
        handleDeleteFooterItem,
        handleDeleteFooterHeader,
    }} >
        {children}
    </ParamContext.Provider>
}
